import {
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from '@tanstack/react-query';
// @ts-ignore
import { QueryFunctionContext } from 'react-query/types/core/types';
import { AxiosError, AxiosResponse } from 'axios';
import { AxiosInstance } from 'axios';
import useAxiosClient from "@/lib/axios";

type QueryKeyT = [string, object | undefined];

export const fetcher = <T>({
                               queryKey,
                               pageParam,
                           }: QueryFunctionContext<QueryKeyT>, axios: AxiosInstance): Promise<T> => {
    const [url, params] = queryKey;
    return axios
        .get<T>(url, { params: { ...params, pageParam } })
        .then((res: AxiosResponse<T>) => res.data);
};

export const usePrefetch = <T>(url: string | null, params?: object) => {
    const queryClient = useQueryClient();
    const axiosClient = useAxiosClient();
    return () => {
        if (!url) {
            return;
        }

        queryClient.prefetchQuery<T, Error, T, QueryKeyT>(
            [url!, params],
            ({ queryKey }) => fetcher({ queryKey, meta: undefined }, axiosClient),
            {
                staleTime: 60000
            }
        );
    };
};

export const useFetch = <T>(
    url: string | null,
    params?: object,
    config?: UseQueryOptions<T, Error, T, QueryKeyT>
) => {
    const axiosClient = useAxiosClient();

    const context = useQuery<T, Error, T, QueryKeyT>(
        [url!, params],
        ({ queryKey }) => fetcher({ queryKey, meta: undefined }, axiosClient),
        {
            enabled: !!url,
            ...config,
        }
    );

    return context;
};

const useGenericMutation = <T, S>(
    func: (data: T | S) => Promise<AxiosResponse<S>>,
    url: string,
    params?: object,
    updater?: ((oldData: T, newData: S) => T) | undefined
) => {
    const queryClient = useQueryClient();

    return useMutation<AxiosResponse, AxiosError, T | S>(func, {
        onMutate: async (data) => {
            await queryClient.cancelQueries([url!, params]);

            const previousData = queryClient.getQueryData([url!, params]);

            queryClient.setQueryData<T>([url!, params], (oldData) => {
                return updater ? updater(oldData!, data as S) : (data as T);
            });

            return previousData;
        },
        onError: (err: AxiosError<any, any>, _, context) => {
            console.error(err);
            queryClient.setQueryData([url!, params], context);
        },
        onSettled: () => {
            queryClient.invalidateQueries([url!, params]);
        },
    });
};

export const useDelete = <T>(
    url: string,
    params?: object,
    updater?: (oldData: T, id: string | number) => T
) => {
    const axiosClient = useAxiosClient();

    return useGenericMutation<T, string | number>(
        (id) => axiosClient.delete(`${url}/${id}`),
        url,
        params,
        updater
    );
};

interface Indexable {
    [key: string]: any;
}

export const usePost = <T extends Indexable, S extends Indexable>(
    url: string,
    params?: object,
    updater?: (oldData: T, newData: S) => T
) => {
    const axiosClient = useAxiosClient();

    return useGenericMutation<T, S>(
        (data ) => {
            const formData = new FormData();
            for (const key in data) {
                formData.append(key, data[key]);
            }
            return axiosClient.post<S>(url, formData);
        },
        url,
        params,
        updater
    );
};

export const useUpdate = <T, S>(
    url: string,
    params?: object,
    updater?: (oldData: T, newData: S) => T
) => {
    const axiosClient = useAxiosClient();
    return useGenericMutation<T, S>(
        (data) => axiosClient.patch<S>(url, data),
        url,
        params,
        updater
    );
};
